import {
  EngagementService,
  IEngagementServiceState,
  IStudentRewardProgress,
  IUser,
  ServiceStatusEnum
} from "@imaginelearning/mps-api-service";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { UserContext } from "../context-providers/user.provider";
import useGrade from "../components/liftoff/hooks/useGrade";
import getGradeband from "../helpers/get-gradeband";

const defaultRewardState: IEngagementServiceState<IStudentRewardProgress> = {
  status: ServiceStatusEnum.LOADING,
  response: ""
};
const defaultDispatchString: React.Dispatch<React.SetStateAction<string>> = (val) => val;
const defaultDispatchObject: React.Dispatch<React.SetStateAction<object>> = (val) => val;
const defaultState = {
  rewardState: defaultRewardState,
  getReward: defaultDispatchString,
  setReward: defaultDispatchString,
  contextCurrentLevelData: {
    level: 1,
    points: 0
  },
  setContextCurrentLevelData: defaultDispatchObject,
  isInitialRender: false,
  addNewRewards: async (): Promise<void> => {
    // Default implementation
  }
};
export const RewardContext = createContext(defaultState);

export function RewardProvider(props: any) {
  const [rewardState, setReward] = useState<IEngagementServiceState<IStudentRewardProgress>>({
    status: ServiceStatusEnum.LOADING,
    response: ""
  });
  const [contextCurrentLevelData, setContextCurrentLevelData] = useState({ level: 1, points: 0 });
  const [forceRefetch, getReward] = useState("");
  const { userState } = useContext(UserContext);
  const { gradeBand } = useGrade();
  const user: IUser = userState.response as IUser;
  const [isInitialRender, setIsInitialRender] = useState(true);

  const addNewRewards = async (pointsEarned: number): Promise<void> => {
    try {
      if (!user.schoolYear) {
        throw new Error("User school year is not available");
      }
      await EngagementService.addNewRewards(user.id, pointsEarned, gradeBand, user.schoolYear);
      // Trigger a refetch of reward progress data after updating
      getReward(Date.now().toString());
    } catch (error) {
      console.error("Error updating rewards:", error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchData = async (gradeband: string) => {
      const result: IEngagementServiceState<IStudentRewardProgress> = await EngagementService.getRewardProgressData(
        user.id,
        gradeband,
        user.schoolYear
      );
      if (isInitialRender) {
        setContextCurrentLevelData({
          level: (result.response as IStudentRewardProgress).currentLevel,
          points: (result.response as IStudentRewardProgress).currentPoints
        });
        setIsInitialRender(false);
      }
      setReward(result);
    };
    if (user?.id) {
      const gradeband = getGradeband(user.gradeLevel);
      if (gradeband !== "9-12") fetchData(gradeband);
    }
  }, [user, forceRefetch, isInitialRender]);

  const value = useMemo(
    () => ({
      rewardState,
      getReward,
      setReward,
      contextCurrentLevelData,
      setContextCurrentLevelData,
      addNewRewards
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rewardState]
  );

  return <RewardContext.Provider value={value} {...props} />;
}
